<template>
    <canvas ref="canvas" height="200" style="width: 100%;" />
</template>

<script>
function timeToMinutes(str) {
    if (!str) return 0
    const lower = str.toLowerCase().trim()
    const isPm  = lower.includes('pm')
    const isAm  = lower.includes('am')
    const clean = lower.replace(/[apm\s]/g, '')
    let [h, m]  = clean.split(':').map(Number)
    if (isPm && h !== 12) h += 12
    if (isAm && h === 12) h = 0
    return h * 60 + (m || 0)
}

export default {
    name: 'SunAltitudeChart',
    props: {
        sunrise:     { type: String,  default: '' },
        sunset:      { type: String,  default: '' },
        noonAlt:     { type: Number,  default: null },
        currentTime: { type: Date,    default: null },
        title:       { type: String,  default: '' },
        subtitle:    { type: String,  default: '' },
        subtitle2:   { type: String,  default: '' },
    },
    watch: {
        noonAlt()     { this.redraw() },
        currentTime() { this.redraw() },
        sunrise()      { this.redraw() },
        sunset()       { this.redraw() },
    },
    mounted() {
        this.redraw()
    },
    methods: {
        redraw() {
            const canvas = this.$refs.canvas
            if (!canvas || this.noonAlt === null || !this.sunrise || !this.sunset) return

            const dpr  = window.devicePixelRatio || 1
            const cssW = canvas.parentElement ? canvas.parentElement.clientWidth : 600
            const cssH = parseInt(canvas.style.height) || canvas.height
            canvas.width  = cssW * dpr
            canvas.height = cssH * dpr
            canvas.style.height = cssH + 'px'
            const ctx = canvas.getContext('2d')
            ctx.scale(dpr, dpr)
            const w = cssW
            const h = cssH

            const PAD_TOP    = 56
            const PAD_BOTTOM = 40
            const PAD_LEFT   = 40
            const PAD_RIGHT  = 16
            const plotW = w - PAD_LEFT - PAD_RIGHT
            const plotH = h - PAD_TOP - PAD_BOTTOM
            const plotBottom = PAD_TOP + plotH

            ctx.clearRect(0, 0, w, h)

            if (this.title) {
                ctx.font = 'bold 11px system-ui, sans-serif'
                ctx.textAlign = 'left'
                ctx.fillStyle = 'rgba(0,0,0,0.6)'
                ctx.fillText(this.title, PAD_LEFT, 16)
            }
            if (this.subtitle) {
                ctx.font = '10px system-ui, sans-serif'
                ctx.textAlign = 'left'
                ctx.fillStyle = 'rgba(0,0,0,0.38)'
                ctx.fillText(this.subtitle, PAD_LEFT, 28)
            }
            if (this.subtitle2) {
                ctx.font = '10px system-ui, sans-serif'
                ctx.textAlign = 'left'
                ctx.fillStyle = 'rgba(0,0,0,0.38)'
                ctx.fillText(this.subtitle2, PAD_LEFT, 40)
            }

            const sunriseMin = timeToMinutes(this.sunrise)
            const sunsetMin  = timeToMinutes(this.sunset)
            const noonMin    = (sunriseMin + sunsetMin) / 2

            // x maps [sunriseMin, sunsetMin] → [PAD_LEFT, PAD_LEFT + plotW]
            const toX = min => PAD_LEFT + ((min - sunriseMin) / (sunsetMin - sunriseMin)) * plotW
            // y maps [0, noonAlt] → [plotBottom, PAD_TOP]
            const toY = alt => plotBottom - (alt / this.noonAlt) * plotH

            const sunriseX = PAD_LEFT
            const sunsetX  = PAD_LEFT + plotW
            const noonX    = toX(noonMin)
            const noonY    = PAD_TOP
            // quadratic bezier control point that passes through noonY at t=0.5
            const cpY      = 2 * noonY - plotBottom

            // Day gradient fill
            ctx.beginPath()
            ctx.moveTo(sunriseX, plotBottom)
            ctx.quadraticCurveTo(noonX, cpY, sunsetX, plotBottom)
            ctx.closePath()
            const dayGrad = ctx.createLinearGradient(0, PAD_TOP, 0, plotBottom)
            dayGrad.addColorStop(0, 'rgba(245, 185, 50, 0.22)')
            dayGrad.addColorStop(1, 'rgba(245, 185, 50, 0.03)')
            ctx.fillStyle = dayGrad
            ctx.fill()

            // Arc curve line
            ctx.beginPath()
            ctx.moveTo(sunriseX, plotBottom)
            ctx.quadraticCurveTo(noonX, cpY, sunsetX, plotBottom)
            ctx.strokeStyle = 'rgba(245, 185, 50, 0.85)'
            ctx.lineWidth = 2
            ctx.setLineDash([])
            ctx.stroke()

            // Horizon / baseline
            ctx.beginPath()
            ctx.moveTo(PAD_LEFT, plotBottom)
            ctx.lineTo(PAD_LEFT + plotW, plotBottom)
            ctx.strokeStyle = 'rgba(0,0,0,0.18)'
            ctx.lineWidth = 1
            ctx.setLineDash([4, 4])
            ctx.stroke()
            ctx.setLineDash([])

            // Y-axis labels
            ctx.font = '10px system-ui, sans-serif'
            ctx.textAlign = 'right'
            ctx.fillStyle = 'rgba(0,0,0,0.35)'
            ctx.fillText('0°', PAD_LEFT - 8, plotBottom + 4)

            // Solar noon vertical + horizontal projections
            ctx.beginPath()
            ctx.moveTo(noonX, PAD_TOP)
            ctx.lineTo(noonX, plotBottom)
            ctx.strokeStyle = 'rgba(200, 60, 60, 0.25)'
            ctx.lineWidth = 1
            ctx.setLineDash([4, 4])
            ctx.stroke()

            ctx.beginPath()
            ctx.moveTo(PAD_LEFT, noonY)
            ctx.lineTo(noonX, noonY)
            ctx.stroke()
            ctx.setLineDash([])

            // Noon dot
            ctx.beginPath()
            ctx.arc(noonX, noonY, 4, 0, Math.PI * 2)
            ctx.fillStyle = '#fff'
            ctx.fill()
            ctx.strokeStyle = 'rgba(200, 60, 60, 0.85)'
            ctx.lineWidth = 2
            ctx.stroke()

            // Noon Y-axis label (red)
            ctx.font = '10px system-ui, sans-serif'
            ctx.textAlign = 'right'
            ctx.fillStyle = 'rgba(200, 60, 60, 0.70)'
            ctx.fillText(Math.round(this.noonAlt) + '°', PAD_LEFT - 8, noonY + 4)

            // Sunrise / Sunset dots + labels
            for (const [mx, timeStr] of [
                [sunriseX, this.sunrise],
                [sunsetX,  this.sunset],
            ]) {
                ctx.beginPath()
                ctx.arc(mx, plotBottom, 4, 0, Math.PI * 2)
                ctx.fillStyle = '#fff'
                ctx.fill()
                ctx.strokeStyle = 'rgba(245, 185, 50, 0.85)'
                ctx.lineWidth = 2
                ctx.stroke()

                ctx.font = '10px system-ui, sans-serif'
                ctx.textAlign = mx === sunriseX ? 'left' : 'right'
                ctx.fillStyle = 'rgba(0,0,0,0.38)'
                ctx.fillText(timeStr.replace(/\s+(am|pm)$/i, '$1'), mx, plotBottom + 14)
            }

            // Noon x-axis label
            ctx.font = '10px system-ui, sans-serif'
            ctx.textAlign = 'center'
            ctx.fillStyle = 'rgba(200, 60, 60, 0.70)'
            ctx.fillText('Noon', noonX, plotBottom + 14)

            // Now marker — only shown when sun is above horizon
            if (this.currentTime instanceof Date) {
                const nowMin = this.currentTime.getHours() * 60 + this.currentTime.getMinutes()
                if (nowMin >= sunriseMin && nowMin <= sunsetMin) {
                    const t      = (nowMin - sunriseMin) / (sunsetMin - sunriseMin)
                    const nowAlt = this.noonAlt * 4 * t * (1 - t)
                    const nowX   = toX(nowMin)
                    const nowY   = toY(nowAlt)

                    // Horizontal projection to Y-axis
                    ctx.beginPath()
                    ctx.moveTo(PAD_LEFT, nowY)
                    ctx.lineTo(nowX, nowY)
                    ctx.strokeStyle = 'rgba(50, 160, 90, 0.25)'
                    ctx.lineWidth = 1
                    ctx.setLineDash([4, 4])
                    ctx.stroke()

                    // Vertical projection to x-axis
                    ctx.beginPath()
                    ctx.moveTo(nowX, nowY)
                    ctx.lineTo(nowX, plotBottom)
                    ctx.stroke()
                    ctx.setLineDash([])

                    // Now dot
                    ctx.beginPath()
                    ctx.arc(nowX, nowY, 4, 0, Math.PI * 2)
                    ctx.fillStyle = '#fff'
                    ctx.fill()
                    ctx.strokeStyle = 'rgba(50, 160, 90, 0.85)'
                    ctx.lineWidth = 2
                    ctx.stroke()

                    // Y-axis label at current altitude — push down if too close to noon label
                    const noonLabelY = noonY + 4
                    let nowLabelY = nowY + 4
                    if (nowLabelY - noonLabelY < 14) nowLabelY = noonLabelY + 14
                    ctx.font = '10px system-ui, sans-serif'
                    ctx.textAlign = 'right'
                    ctx.fillStyle = 'rgba(50, 160, 90, 0.85)'
                    ctx.fillText(Math.round(nowAlt) + '°', PAD_LEFT - 8, nowLabelY)

                    // X-axis time label (second row)
                    const nowH  = this.currentTime.getHours()
                    const nowM  = String(this.currentTime.getMinutes()).padStart(2, '0')
                    const ampm  = nowH >= 12 ? 'pm' : 'am'
                    const h12   = nowH % 12 || 12
                    ctx.textAlign = 'center'
                    ctx.fillStyle = 'rgba(50, 160, 90, 0.85)'
                    ctx.fillText(`${h12}:${nowM}${ampm}`, nowX, plotBottom + 28)
                }
            }
        },
    },
}
</script>
